import { useState } from 'react';
import { Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { Link as RouterLink, Navigate, useParams } from 'react-router-dom';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { EmptyState } from '../components/EmptyState';
import { Meta } from '../components/Meta';
import { TabStrip } from '../components/TabStrip';
import { StatusChip, StatusDot } from '../components/StatusChip';
import { Panel } from '../components/Panel';
import { ShipmentFinancials } from '../features/finance/FinancialsTabs';
import { ModeIcon, RouteText } from '../features/shipments/ShipmentCard';
import { StageStepper } from '../features/shipments/StageStepper';
import {
  CargoPanel,
  DocumentsPanel,
  KeyInfoPanel,
  PartiesPanel,
  RoutePanel,
  StoryPanel,
  TimelinePanel,
} from '../features/shipments/ShipmentPanels';
import { useApprovalsFor } from '../features/approvals/useApprovals';
import { detailOf, STAGE_TONE, STEPS, stepOf } from '../features/shipments/sample';
import { useShipments } from '../features/shipments/useShipments';
import { useDocuments } from '../features/documents/useDocuments';

const TABS = [
  'Overview',
  'Timeline',
  'Rates & Quotations',
  'Communications',
  'Documents',
  'Financials',
  'Activity',
] as const;
const GAP = { xs: 1.5, md: 2 };

/** One shipment: where it is, what happened, who is involved, the cargo and the papers */
export function ShipmentPage() {
  const { srb } = useParams();
  const [tab, setTab] = useState<(typeof TABS)[number]>('Overview');
  const docs = useDocuments();
  const approvalsFor = useApprovalsFor();
  const { items, loading } = useShipments();
  const s = items.find((x) => x.srb === srb);
  if (loading) return <EmptyState>Loading the shipment…</EmptyState>;
  if (!s) return <Navigate to="/shipments" replace />;
  const d = detailOf(s);
  const records = docs.forShipment(s.srb);
  const approvals = approvalsFor(s.srb);

  return (
    <Stack spacing={GAP}>
      <Button component={RouterLink} to="/shipments" startIcon={<ArrowBack />} sx={{ alignSelf: 'flex-start' }}>
        Shipments
      </Button>

      <Box
        sx={{
          display: 'grid',
          gap: GAP,
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', xl: 'minmax(0, 1fr) minmax(0, 1.6fr)' },
          alignItems: 'start',
        }}
      >
        <Box>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="h4" component="h1">
              {s.srb}
            </Typography>
            <StatusChip label={s.stage} tone={STAGE_TONE[s.stage]} />
          </Stack>
          <Typography sx={{ fontWeight: 600, mt: 0.5 }}>
            <RouteText s={s} />
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1, alignItems: 'center' }}>
            <Chip size="small" variant="outlined" label={s.customer} />
            <Chip size="small" variant="outlined" icon={<ModeIcon mode={s.mode} />} label={s.service} />
            <Chip size="small" variant="outlined" label={s.incoterm} />
            <Chip size="small" variant="outlined" label={s.cargo} />
            <Typography variant="caption" color="text.secondary">
              {s.load}
            </Typography>
          </Stack>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: 'minmax(0, 1fr) minmax(0, 2fr) minmax(0, 1fr)',
            },
          }}
        >
          <Fact label="ETA" value={s.eta} big />
          <Fact
            label="Next Action"
            value={
              <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                <StatusDot tone={s.next.tone} />
                <span>{s.next.text}</span>
              </Stack>
            }
          />
          <Fact
            label="Pending Approvals"
            value={
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <span>{approvals}</span>
                {approvals > 0 && (
                  <Button component={RouterLink} to="/approvals" size="small">
                    View
                  </Button>
                )}
              </Stack>
            }
            big
          />
        </Box>
      </Box>

      <Card>
        <CardContent>
          <StageStepper steps={STEPS} current={stepOf(s.stage)} dates={d.stepDates} />
        </CardContent>
      </Card>

      <TabStrip tabs={TABS.map((t) => ({ value: t }))} value={tab} onChange={setTab} />

      {tab === 'Overview' && (
        <Box
          sx={{
            display: 'grid',
            gap: GAP,
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', xl: 'minmax(0, 1fr) 380px' },
            alignItems: 'start',
          }}
        >
          <Stack spacing={GAP}>
            <StoryPanel d={d} />
            <TimelinePanel d={d} />
            <Box
              sx={{
                display: 'grid',
                gap: GAP,
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' },
              }}
            >
              <CargoPanel packages={d.packages} />
              <RoutePanel route={d.route} />
            </Box>
          </Stack>
          <Box
            sx={{
              display: 'grid',
              gap: GAP,
              gridTemplateColumns: { xs: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', xl: 'minmax(0, 1fr)' },
              alignContent: 'start',
            }}
          >
            <KeyInfoPanel d={d} />
            <PartiesPanel parties={d.parties} />
            <DocumentsPanel records={records} />
          </Box>
        </Box>
      )}
      {tab === 'Timeline' && <TimelinePanel d={d} />}
      {tab === 'Documents' && <DocumentsPanel records={records} />}
      {tab === 'Financials' && <ShipmentFinancials srb={s.srb} />}
      {(tab === 'Rates & Quotations' || tab === 'Communications' || tab === 'Activity') && (
        <Panel title={tab}>
          <EmptyState>This tab is coming soon.</EmptyState>
        </Panel>
      )}
    </Stack>
  );
}

function Fact(props: { label: string; value: React.ReactNode; big?: boolean }) {
  return (
    <Card>
      <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
        <Meta {...props} />
      </CardContent>
    </Card>
  );
}
